/**
 * commands.js — the ask-bar command language, as pure functions.
 *
 * parseCommand(text)  classifies what the user typed (voice will reuse this):
 *   "open …" | "show …" | "go to …" | "isolate …"  -> { type: 'resolve' }
 *   "deeper" | "go deeper"                         -> { type: 'deeper' }
 *   "back" | "up" | "go back" | "go up"            -> { type: 'up' }
 *   "reset" | "reset view"                         -> { type: 'reset' }
 *   "show me why/how/what …"                       -> { type: 'ask' }
 *   anything else                                  -> { type: 'ask' }
 *
 * planDrill(command, view) turns a navigation command into a view action
 * without touching React state, so the rules are unit-testable:
 *   { select?, isolate?, t?, frame?: string[], message? }
 * `isolate: null` means "clear isolation"; an absent key means "leave as is".
 */

const RESOLVE_PREFIXES = ['go to ', 'open ', 'show ', 'isolate '];
const SHOW_QUESTION = /^(?:show|tell)(?: me| us)? (?:why|how|what|whether|if|where|when|which)\b/;
// Lead-ins, including a barge-in's "Stop. Show me the screws" (same list as mhi_voice/commands.py).
const LEAD = /^(?:(?:ok(?:ay)?|hey|so|now|please|stop|wait|no|sorry|hold on)[,.!]*[, ]+)+/i;
// "show me the pump" is the whole model, not a part named "pump".
const WHOLE = new Set(['everything', 'all', 'it all', 'whole thing', 'the whole thing', 'whole model',
  'the whole model', 'the machine', 'the pump', 'the vacuum pump', 'the jet', 'the aircraft', 'the asset']);

/** Explode level every drill-down guarantees, so the target is readable. */
export const DRILL_MIN_EXPLODE = 0.5;
const EXPLODE_STEP = 0.25;

export function parseCommand(text) {
  const original = String(text ?? '').trim();
  // Lead-ins are dropped for command matching only; a question keeps its own words.
  const raw = original.replace(/\s+/g, ' ').replace(LEAD, '');
  if (!raw) return { type: 'empty' };
  const lower = raw.toLowerCase().replace(/[.!?]+$/, '').trim();

  if (lower === 'reset' || lower === 'reset view') return { type: 'reset' };
  if (lower === 'deeper' || lower === 'go deeper') return { type: 'deeper' };
  if (['back', 'up', 'go back', 'go up'].includes(lower)) return { type: 'up' };
  // "show me why the separator pressure is high" asks a question; it is not "show <part>".
  if (SHOW_QUESTION.test(lower)) return { type: 'ask', question: original };

  for (const prefix of RESOLVE_PREFIXES) {
    if (lower.startsWith(prefix)) {
      const query = raw.slice(prefix.length).trim();
      if (WHOLE.has(lower.slice(prefix.length).replace(/^(?:me|us) /, '').trim())) return { type: 'reset' };
      if (query) return { type: 'resolve', text: raw, query };
    }
  }
  return { type: 'ask', question: original };
}

const hasChildren = (tree, id) => (tree.childrenOf(id) ?? []).length > 0;

/**
 * @param {{type:string}} command  from parseCommand
 * @param {{tree:object, isolatedId:?string, selectedId:?string, t:number}} view
 */
export function planDrill(command, { tree, isolatedId = null, selectedId = null, t = 0 }) {
  if (!tree?.byId) return { message: 'Assembly not loaded yet' };
  const known = (id) => Boolean(id && tree.byId.has(id));
  const current = known(isolatedId) ? isolatedId : (known(selectedId) ? selectedId : null);
  const atLeast = Math.max(t, DRILL_MIN_EXPLODE);

  if (command.type === 'deeper') {
    if (!current) {
      const root = tree.roots.find((id) => hasChildren(tree, id)) ?? tree.roots[0];
      if (!root) return { message: 'Nothing to drill into' };
      return { select: root, isolate: root, t: atLeast, frame: [root] };
    }
    const kids = tree.childrenOf(current) ?? [];
    const branch = kids.find((id) => hasChildren(tree, id));
    if (branch) return { select: branch, isolate: branch, t: atLeast, frame: [branch] };
    if (t < 1) {
      return {
        isolate: kids.length ? current : undefined,
        t: Math.min(1, Math.max(atLeast, t + EXPLODE_STEP)),
        frame: [current],
        message: 'Unit level — exploding further',
      };
    }
    if (kids.length) return { select: kids[0], isolate: kids[0], frame: [kids[0]] };
    return { message: 'Already at the deepest level' };
  }

  if (command.type === 'up') {
    if (!current) return { isolate: null, message: 'Already at the top' };
    const parent = tree.parentOf(current);
    if (parent) return { select: parent, isolate: parent, t: atLeast, frame: [parent] };
    return { isolate: null, select: current, message: 'Whole asset' };
  }

  return {};
}

/**
 * Pick the best resolve match that exists in the loaded tree.
 * @param {{cad_id:string}[]} matches
 */
export function planResolved(matches, { tree, t = 0 }) {
  const best = (matches ?? []).find((m) => m && tree?.byId?.has(m.cad_id));
  if (!best) return null;
  return {
    select: best.cad_id,
    isolate: best.cad_id,
    t: Math.max(t, DRILL_MIN_EXPLODE),
    frame: [best.cad_id],
    match: best,
  };
}
